import { useEffect, useState, useMemo } from "react";
import { useAuth } from "../context/AuthContext";
import { useLanguage } from "../context/LanguageContext";
import { LanguageSwitcher } from "../components/LanguageSwitcher";
import api, { formatError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../components/ui/alert-dialog";
import { Checkbox } from "../components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "../components/ui/dropdown-menu";
import { Popover, PopoverTrigger, PopoverContent } from "../components/ui/popover";
import { Calendar as CalendarPicker } from "../components/ui/calendar";
import { exportCSV, exportExcel, exportPDF, exportWord, exportUsersCSV, exportUsersExcel, exportUsersPDF, exportUsersWord } from "../lib/exporters";
import { toast } from "sonner";
import { LogOut, Trophy, Plus, ShieldCheck, User as UserIcon, Download, Filter, X, Calendar as CalIcon, Trash2, Search, NotebookPen, RotateCcw, Minus } from "lucide-react";
import { format } from "date-fns";
import { TournamentCard, formatTime12h, formatDmy } from "./UserDashboard";
import { API_BASE } from "../lib/api";

const SPORTS = [
    "Grassroots Football",
    "Amateur Football",
    "Youth Football U14-U19",
    "Futsal",
    "Mini Football",
    "Padel",
    "Basketball",
    "Tennis",
];

const PAYMENT_METHODS = [
    "ContactPay (Eurobank)",
    "QuickPay (Bank of Cyprus)",
    "Revolut",
    "IBAN transfer",
    "Cash",
];


export default function AdminDashboard() {
    const { user, logout } = useAuth();
    const { t: tr } = useLanguage();
    const [users, setUsers] = useState([]);
    const [availability, setAvailability] = useState([]);
    const [tournaments, setTournaments] = useState([]);
    const [deletedTournaments, setDeletedTournaments] = useState([]);
    const [editingTournament, setEditingTournament] = useState(null);
    const [cloningTournament, setCloningTournament] = useState(null);
    const [tournamentToDelete, setTournamentToDelete] = useState(null);

    const load = async () => {
        try {
            const [u, av, t, d] = await Promise.all([
                api.get("/admin/users"),
                api.get("/admin/availability"),
                api.get("/tournaments"),
                api.get("/admin/tournaments/deleted"),
            ]);
            setUsers(u.data);
            setAvailability(av.data);
            setTournaments(t.data);
            setDeletedTournaments(d.data);
        } catch (err) {
            toast.error(formatError(err.response?.data?.detail) || err.message);
        }
    };

    useEffect(() => {
        load();
    }, []);

    const deleteTournament = async (id) => {
        try {
            await api.delete(`/tournaments/${id}`);
            toast.success(tr("admin.tournamentRemoved"));
            await load();
        } catch (err) {
            toast.error(formatError(err.response?.data?.detail) || err.message);
        }
    };

    const requestDeleteTournament = (id) => {
        const t = tournaments.find((x) => x.id === id);
        if (t) setTournamentToDelete(t);
    };

    const confirmSoftDeleteTournament = async () => {
        if (!tournamentToDelete) return;
        const id = tournamentToDelete.id;
        setTournamentToDelete(null);
        await deleteTournament(id);
    };

    const restoreTournament = async (id) => {
        try {
            await api.post(`/admin/tournaments/${id}/restore`);
            toast.success(tr("admin.tournamentRestored"));
            await load();
        } catch (err) {
            toast.error(formatError(err.response?.data?.detail) || err.message);
        }
    };

    const permanentlyDeleteTournament = async (id) => {
        try {
            await api.delete(`/admin/tournaments/${id}/permanent`);
            toast.success(tr("admin.tournamentPermDeleted"));
            await load();
        } catch (err) {
            toast.error(formatError(err.response?.data?.detail) || err.message);
        }
    };

    const deleteUsers = async (ids) => {
        const results = await Promise.allSettled(ids.map((id) => api.delete(`/admin/users/${id}`)));
        const ok = results.filter((r) => r.status === "fulfilled").length;
        const fail = results.length - ok;
        if (ok > 0) toast.success(tr("admin.deletedCount", { n: ok }));
        if (fail > 0) toast.error(tr("admin.deleteFailedCount", { n: fail }));
        await load();
    };

    return (
        <div className="min-h-screen bg-background">
            <header className="glass sticky top-0 z-20 border-b border-border">
                <div className="max-w-7xl mx-auto px-4 sm:px-8 h-16 flex items-center justify-between gap-3">
                    <div className="flex items-center gap-1 min-w-0 flex-shrink">
                        <img src="https://customer-assets.emergentagent.com/job_schedule-sync-109/artifacts/0i7pk9q1_refpairlogo_trans.png" alt="RefPair" className="w-12 h-12 object-contain flex-shrink-0" />
                        <div className="min-w-0">
                            <div className="font-heading text-sm font-semibold leading-tight truncate">RefPair<span className="text-muted-foreground font-normal text-[0.78em] tracking-tighter -ml-[1px]">.com</span> · {tr("brand.admin")}</div>
                            <div className="label-mono text-[10px] leading-tight truncate">{tr("brand.controlRoom")}</div>
                        </div>
                    </div>
                    <div className="flex items-center gap-2 sm:gap-4 flex-shrink-0">
                        <LanguageSwitcher variant="compact" />
                        <div className="text-right hidden md:block">
                            <div className="text-sm font-medium truncate max-w-[160px]" data-testid="admin-display-name">{user.name}</div>
                            <div className="label-mono text-[10px] truncate max-w-[160px]">{user.email}</div>
                        </div>
                        <Button variant="outline" size="sm" onClick={logout} className="rounded-md flex-shrink-0" data-testid="admin-logout-btn">
                            <LogOut className="w-4 h-4 sm:mr-1" /> <span className="hidden sm:inline">{tr("common.signOut")}</span>
                        </Button>
                    </div>
                </div>
            </header>

            <main className="max-w-7xl mx-auto px-8 py-10 space-y-8">
                <div className="flex items-center justify-between">
                    <div>
                        <div className="label-mono mb-2">{tr("admin.kicker")}</div>
                        <h1 className="font-heading text-3xl sm:text-4xl font-semibold">{tr("admin.title")}</h1>
                    </div>
                    <div className="flex gap-4">
                        <Stat label={tr("admin.statReferees")} value={users.length} testid="stat-users" />
                        <Stat label={tr("admin.statSlots")} value={availability.length} testid="stat-slots" />
                        <Stat label={tr("admin.statTournaments")} value={tournaments.length} testid="stat-tournaments" />
                    </div>
                </div>

                <Tabs defaultValue="users" className="w-full">
                    <TabsList className="bg-transparent border-b border-border rounded-none w-full justify-start gap-6 p-0 h-auto">
                        <TabsTrigger
                            value="users"
                            className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none px-0 pb-3 font-heading"
                            data-testid="tab-users"
                        >
                            {tr("admin.tabUsers")}
                        </TabsTrigger>
                        <TabsTrigger
                            value="tournaments"
                            className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none px-0 pb-3 font-heading"
                            data-testid="tab-tournaments"
                        >
                            {tr("admin.tabTournaments")}
                        </TabsTrigger>
                        <TabsTrigger
                            value="restore"
                            className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none px-0 pb-3 font-heading"
                            data-testid="tab-restore"
                        >
                            {tr("admin.tabRestore")}{deletedTournaments.length ? ` (${deletedTournaments.length})` : ""}
                        </TabsTrigger>
                    </TabsList>

                    <TabsContent value="users" className="pt-6 space-y-8">
                        <UsersSection users={users} availability={availability} onDeleteUsers={deleteUsers} onUsersChanged={load} />
                    </TabsContent>

                    <TabsContent value="tournaments" className="pt-6 space-y-6">
                        <div className="flex items-center justify-between">
                            <h2 className="font-heading text-xl font-medium">{tr("admin.allTournaments")}</h2>
                            <TournamentDialog
                                onCreated={load}
                                editing={editingTournament} onClearEditing={() => setEditingTournament(null)}
                                cloning={cloningTournament} onClearCloning={() => setCloningTournament(null)}
                            />
                        </div>
                        {tournaments.length === 0 ? (
                            <div className="bg-white border border-border rounded-md p-10 text-center text-muted-foreground" data-testid="admin-tournaments-empty">
                                {tr("admin.noTournamentsAdmin")}
                            </div>
                        ) : (
                            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 stagger-in" data-testid="admin-tournaments-grid">
                                {tournaments.map((t) => (
                                    <TournamentCard key={t.id} tournament={t} onDelete={requestDeleteTournament} onEdit={setEditingTournament} onClone={setCloningTournament} />
                                ))}
                            </div>
                        )}
                    </TabsContent>

                    <TabsContent value="restore" className="pt-6 space-y-6">
                        <h2 className="font-heading text-xl font-medium">{tr("admin.restoreHeading")}</h2>
                        {deletedTournaments.length === 0 ? (
                            <div className="bg-white border border-border rounded-md p-10 text-center text-muted-foreground" data-testid="admin-restore-empty">
                                {tr("admin.restoreEmpty")}
                            </div>
                        ) : (
                            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 stagger-in" data-testid="admin-restore-grid">
                                {deletedTournaments.map((t) => (
                                    <DeletedTournamentCard key={t.id} tournament={t} onRestore={restoreTournament} onPermanentlyDelete={permanentlyDeleteTournament} />
                                ))}
                            </div>
                        )}
                    </TabsContent>
                </Tabs>
            </main>

            <AlertDialog open={!!tournamentToDelete} onOpenChange={(o) => { if (!o) setTournamentToDelete(null); }}>
                <AlertDialogContent data-testid="soft-delete-confirm-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle>{tr("admin.softDeleteConfirmTitle")}</AlertDialogTitle>
                        <AlertDialogDescription>
                            {tr("admin.softDeleteConfirmBody")}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="soft-delete-cancel-btn">{tr("admin.softDeleteConfirmNo")}</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={(e) => { e.preventDefault(); confirmSoftDeleteTournament(); }}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                            data-testid="soft-delete-confirm-btn"
                        >
                            {tr("admin.softDeleteConfirmYes")}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}

function Stat({ label, value, testid }) {
    return (
        <div className="border border-border rounded-md px-5 py-3 bg-white min-w-[120px]" data-testid={testid}>
            <div className="label-mono">{label}</div>
            <div className="mono text-2xl font-medium tracking-tight">{String(value).padStart(2, "0")}</div>
        </div>
    );
}

function DeletedTournamentCard({ tournament, onRestore, onPermanentlyDelete }) {
    const { t: tr } = useLanguage();
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [restoreOpen, setRestoreOpen] = useState(false);
    const deletedOn = tournament.deleted_at ? new Date(tournament.deleted_at) : null;
    return (
        <div className="bg-white border border-border rounded-md overflow-hidden opacity-90" data-testid={`deleted-tournament-card-${tournament.id}`}>
            <TournamentCard tournament={tournament} />
            <div className="p-5 pt-0 -mt-1 space-y-3 border-t border-border bg-accent/20">
                {deletedOn && (
                    <div className="label-mono text-[10px] pt-3">
                        {tr("admin.deletedOn")}: <span className="mono">{deletedOn.toLocaleString()}</span>
                    </div>
                )}
                <Button
                    onClick={() => setRestoreOpen(true)}
                    className="w-full h-10 rounded-md bg-primary text-primary-foreground hover:bg-primary/90"
                    data-testid={`restore-tournament-btn-${tournament.id}`}
                >
                    <RotateCcw className="w-4 h-4 mr-1.5" /> {tr("admin.restoreTournament")}
                </Button>
                <Button
                    variant="outline"
                    onClick={() => setConfirmOpen(true)}
                    className="w-full h-10 rounded-md border-destructive/40 text-destructive hover:bg-destructive/10 hover:text-destructive"
                    data-testid={`perm-delete-tournament-btn-${tournament.id}`}
                >
                    <Trash2 className="w-4 h-4 mr-1.5" /> {tr("admin.permanentlyDelete")}
                </Button>
            </div>

            <AlertDialog open={restoreOpen} onOpenChange={setRestoreOpen}>
                <AlertDialogContent data-testid={`restore-confirm-dialog-${tournament.id}`}>
                    <AlertDialogHeader>
                        <AlertDialogTitle>{tr("admin.restoreConfirmTitle")}</AlertDialogTitle>
                        <AlertDialogDescription>
                            {tr("admin.restoreConfirmBody")}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid={`restore-cancel-${tournament.id}`}>{tr("admin.restoreConfirmCancel")}</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={(e) => { e.preventDefault(); setRestoreOpen(false); onRestore(tournament.id); }}
                            data-testid={`restore-confirm-${tournament.id}`}
                        >
                            {tr("admin.restoreConfirmAction")}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
                <AlertDialogContent data-testid={`perm-delete-confirm-dialog-${tournament.id}`}>
                    <AlertDialogHeader>
                        <AlertDialogTitle>{tr("admin.permDeleteConfirmTitle")}</AlertDialogTitle>
                        <AlertDialogDescription>
                            {tr("admin.permDeleteConfirmBody")}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid={`perm-delete-cancel-${tournament.id}`}>{tr("admin.permDeleteConfirmCancel")}</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={(e) => { e.preventDefault(); setConfirmOpen(false); onPermanentlyDelete(tournament.id); }}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                            data-testid={`perm-delete-confirm-${tournament.id}`}
                        >
                            {tr("admin.permDeleteConfirmAction")}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}

function UsersSection({ users, availability, onDeleteUsers, onUsersChanged }) {
    const { t: tr } = useLanguage();
    const [fromDate, setFromDate] = useState("");
    const [toDate, setToDate] = useState("");
    const [fromOpen, setFromOpen] = useState(false);
    const [toOpen, setToOpen] = useState(false);
    const [selected, setSelected] = useState(new Set());
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [search, setSearch] = useState("");
    const [selectedAvail, setSelectedAvail] = useState(new Set());
    const [confirmAvailOpen, setConfirmAvailOpen] = useState(false);
    const [deletingAvail, setDeletingAvail] = useState(false);

    const filteredUsers = useMemo(() => {
        const q = search.trim().toLowerCase();
        const base = q
            ? users.filter((u) => {
                const sportExp = Array.isArray(u.sports_experience) && u.sports_experience.length > 0
                    ? u.sports_experience.map((s) => `${s.sport || ""} ${s.years || ""}`).join(" ")
                    : (u.interested_sports || "");
                const payments = Array.isArray(u.payment_methods) && u.payment_methods.length > 0
                    ? u.payment_methods.map((p) => `${p.method || ""} ${p.details || ""}`).join(" ")
                    : "";
                const status = u.email_verified ? "verified" : "pending";
                const fields = [
                    u.name,
                    u.surname,
                    status,
                    u.email,
                    u.birth_year,
                    u.mobile,
                    u.location,
                    sportExp,
                    payments,
                ].map((v) => (v ?? "").toString().toLowerCase());
                return fields.some((f) => f.includes(q));
            })
            : users;
        // Pending first, then Verified — preserve original order within each group
        return [...base].sort((a, b) => Number(!!a.email_verified) - Number(!!b.email_verified));
    }, [users, search]);

    const toggleOne = (id) => {
        setSelected((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };
    const toggleAll = () => {
        if (selected.size === filteredUsers.length) setSelected(new Set());
        else setSelected(new Set(filteredUsers.map((u) => u.id)));
    };

    const confirmDelete = async () => {
        setDeleting(true);
        try {
            await onDeleteUsers(Array.from(selected));
            setSelected(new Set());
            setConfirmOpen(false);
        } finally {
            setDeleting(false);
        }
    };

    const allSelected = filteredUsers.length > 0 && filteredUsers.every((u) => selected.has(u.id));

    const [notesUser, setNotesUser] = useState(null);
    const [notesValue, setNotesValue] = useState("");
    const [editUser, setEditUser] = useState(null);
    const [editForm, setEditForm] = useState({ name: "", surname: "", email: "", mobile: "", birth_year: "", location: "" });
    const [editSports, setEditSports] = useState([]);
    const [editPayments, setEditPayments] = useState([]);
    const [editConfirmOpen, setEditConfirmOpen] = useState(false);
    const [editSaving, setEditSaving] = useState(false);

    const openEditUser = (u) => {
        setEditUser(u);
        setEditForm({
            name: u.name || "",
            surname: u.surname || "",
            email: u.email || "",
            mobile: u.mobile || "",
            birth_year: u.birth_year != null ? String(u.birth_year) : "",
            location: u.location || "",
        });
        setEditSports(Array.isArray(u.sports_experience) && u.sports_experience.length > 0
            ? u.sports_experience.map((s) => ({ sport: s.sport || "", years: s.years || "" }))
            : []);
        setEditPayments(Array.isArray(u.payment_methods) && u.payment_methods.length > 0
            ? u.payment_methods.map((p) => ({ method: p.method || "", details: p.details || "" }))
            : []);
    };

    const submitEditUser = async () => {
        if (!editUser) return;
        setEditSaving(true);
        try {
            const payload = {};
            const trimSet = (key, val) => {
                const v = (val ?? "").toString().trim();
                if (v) payload[key] = v;
            };
            trimSet("name", editForm.name);
            trimSet("surname", editForm.surname);
            trimSet("email", editForm.email);
            trimSet("mobile", editForm.mobile);
            trimSet("location", editForm.location);
            if (editForm.birth_year.trim()) {
                const yr = parseInt(editForm.birth_year.trim(), 10);
                if (!Number.isNaN(yr)) payload.birth_year = yr;
            }
            // Always send sports/payments arrays — empty array clears them.
            payload.sports_experience = editSports
                .map((s) => ({ sport: (s.sport || "").trim(), years: (s.years || "").toString().trim() }))
                .filter((s) => s.sport);
            payload.payment_methods = editPayments
                .map((p) => ({ method: (p.method || "").trim(), details: (p.details || "").trim() }))
                .filter((p) => p.method);

            const res = await api.put(`/admin/users/${editUser.id}/profile`, payload);
            void res;
            // Refresh parent state with latest data.
            try { await onUsersChanged(); } catch (_) { /* ignore refresh failures */ }
            toast.success(tr("admin.profileUpdated"));
            setEditConfirmOpen(false);
            setEditUser(null);
        } catch (err) {
            toast.error(formatError(err.response?.data?.detail) || err.message || "Something went wrong. Please try again.");
        } finally {
            setEditSaving(false);
        }
        // Re-fetch in the background; ignore failures here so they don't override the success toast.
        try { await onUsersChanged(); } catch (_) { /* no-op */ }
    };


    const [savingNotes, setSavingNotes] = useState(false);

    const openNotes = (u) => {
        setNotesUser(u);
        setNotesValue(u.admin_notes || "");
    };
    const closeNotes = () => {
        if (savingNotes) return;
        setNotesUser(null);
        setNotesValue("");
    };
    const saveNotes = async () => {
        if (!notesUser) return;
        setSavingNotes(true);
        try {
            await api.put(`/admin/users/${notesUser.id}/notes`, { notes: notesValue });
            toast.success(tr("admin.notesSaved"));
            await onUsersChanged?.();
            setNotesUser(null);
            setNotesValue("");
        } catch (err) {
            toast.error(formatError(err.response?.data?.detail) || err.message);
        } finally {
            setSavingNotes(false);
        }
    };

    const setVerification = async (userId, verified) => {
        try {
            await api.put(`/admin/users/${userId}/verification`, { email_verified: verified });
            toast.success(verified ? tr("admin.activated") : tr("admin.deactivated"));
            await onUsersChanged?.();
        } catch (err) {
            toast.error(formatError(err.response?.data?.detail) || err.message);
        }
    };

    // Per-availability admin notes
    const [availNotesRow, setAvailNotesRow] = useState(null);
    const [availNotesValue, setAvailNotesValue] = useState("");
    const [savingAvailNotes, setSavingAvailNotes] = useState(false);
    const [availSearch, setAvailSearch] = useState("");

    const openAvailNotes = (a) => {
        setAvailNotesRow(a);
        setAvailNotesValue(a.admin_notes || "");
    };
    const closeAvailNotes = () => {
        if (savingAvailNotes) return;
        setAvailNotesRow(null);
        setAvailNotesValue("");
    };
    const saveAvailNotes = async () => {
        if (!availNotesRow) return;
        setSavingAvailNotes(true);
        try {
            await api.put(`/admin/availability/${availNotesRow.id}/notes`, { notes: availNotesValue });
            toast.success(tr("admin.notesSaved"));
            await onUsersChanged?.();
            setAvailNotesRow(null);
            setAvailNotesValue("");
        } catch (err) {
            toast.error(formatError(err.response?.data?.detail) || err.message);
        } finally {
            setSavingAvailNotes(false);
        }
    };

    const filtered = useMemo(() => {
        const q = availSearch.trim().toLowerCase();
        const list = availability.filter((a) => {
            if (fromDate && a.date < fromDate) return false;
            if (toDate && a.date > toDate) return false;
            if (q) {
                const haystack = [
                    a.work_option,
                    a.name,
                    a.surname,
                    a.email,
                    a.mobile,
                    a.notes,
                    a.SuggestedPricePH,
                ].map((v) => (v || "").toString().toLowerCase());
                if (!haystack.some((s) => s.includes(q))) return false;
            }
            return true;
        });
        list.sort((a, b) => {
            if (a.date !== b.date) return a.date < b.date ? 1 : -1;
            return (a.start_time || "") < (b.start_time || "") ? 1 : -1;
        });
        return list;
    }, [availability, fromDate, toDate, availSearch]);

    const toggleAvailOne = (id) => {
        setSelectedAvail((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };
    const toggleAvailAll = () => {
        if (selectedAvail.size === filtered.length) setSelectedAvail(new Set());
        else setSelectedAvail(new Set(filtered.map((a) => a.id)));
    };
    const allAvailSelected = filtered.length > 0 && filtered.every((a) => selectedAvail.has(a.id));
    const performDeleteAvailability = async () => {
        if (selectedAvail.size === 0) return;
        setDeletingAvail(true);
        try {
            await api.post("/admin/availability/bulk-delete", { ids: Array.from(selectedAvail) });
            toast.success(tr("admin.availabilityDeleted", { n: selectedAvail.size }));
            setSelectedAvail(new Set());
            setConfirmAvailOpen(false);
            try { await onUsersChanged(); } catch (_) { /* ignore */ }
        } catch (err) {
            toast.error(formatError(err.response?.data?.detail) || err.message);
        } finally {
            setDeletingAvail(false);
        }
    };

    const fromObj = fromDate ? new Date(`${fromDate}T00:00:00`) : undefined;
    const toObj = toDate ? new Date(`${toDate}T00:00:00`) : undefined;
    const ymd = (d) => format(d, "yyyy-MM-dd");

    const onExport = (kind) => {
        if (filtered.length === 0) {
            toast.error(tr("admin.exportEmpty"));
            return;
        }
        const filename = `referee-availability-${new Date().toISOString().slice(0, 10)}`;
        try {
            if (kind === "csv") exportCSV(filtered, `${filename}.csv`);
            else if (kind === "excel") exportExcel(filtered, `${filename}.xls`);
            else if (kind === "word") exportWord(filtered, `${filename}.doc`);
            else if (kind === "pdf") exportPDF(filtered, `${filename}.pdf`);
            toast.success(tr("admin.exportSuccess", { n: filtered.length, kind: kind.toUpperCase() }));
        } catch (e) {
            toast.error(e.message || tr("admin.exportFailed"));
        }
    };

    const clearFilter = () => { setFromDate(""); setToDate(""); };

    const onExportUsers = (kind) => {
        if (filteredUsers.length === 0) {
            toast.error(tr("admin.exportEmpty"));
            return;
        }
        const filename = `registered-referees-${new Date().toISOString().slice(0, 10)}`;
        try {
            if (kind === "csv") exportUsersCSV(filteredUsers, `${filename}.csv`);
            else if (kind === "excel") exportUsersExcel(filteredUsers, `${filename}.xls`);
            else if (kind === "word") exportUsersWord(filteredUsers, `${filename}.doc`);
            else if (kind === "pdf") exportUsersPDF(filteredUsers, `${filename}.pdf`);
            toast.success(tr("admin.exportSuccess", { n: filteredUsers.length, kind: kind.toUpperCase() }));
        } catch (e) {
            toast.error(e.message || tr("admin.exportFailed"));
        }
    };

    return (
        <div className="space-y-8">
            <div>
                <div className="flex items-center justify-between mb-3 flex-wrap gap-3">
                    <h2 className="font-heading text-xl font-medium">{tr("admin.registeredReferees")}</h2>
                    <div className="flex items-center gap-3 flex-wrap">
                        <div className="relative">
                            <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
                            <Input
                                type="search"
                                placeholder={tr("admin.searchPlaceholder")}
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                className="h-10 rounded-md pl-9 w-72"
                                data-testid="users-search-input"
                            />
                        </div>
                        {selected.size > 0 && (
                            <Button variant="outline" onClick={() => setConfirmOpen(true)}
                                className="rounded-md h-10 border-destructive/40 text-destructive hover:bg-destructive/10 hover:text-destructive"
                                data-testid="delete-selected-users-btn">
                                <Trash2 className="w-4 h-4 mr-1.5" /> {tr("admin.deleteSelected", { n: selected.size })}
                            </Button>
                        )}
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button variant="outline" className="rounded-md h-10 bg-orange-100 border-orange-300 text-orange-900 hover:bg-orange-200 hover:text-orange-900" data-testid="export-users-trigger-btn">
                                    <Download className="w-4 h-4 mr-1.5" /> {tr("admin.exportLabel", { n: filteredUsers.length })}
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" data-testid="export-users-menu">
                                <DropdownMenuItem onClick={() => onExportUsers("csv")} data-testid="export-users-csv">CSV (.csv)</DropdownMenuItem>
                                <DropdownMenuItem onClick={() => onExportUsers("excel")} data-testid="export-users-excel">Excel (.xls)</DropdownMenuItem>
                                <DropdownMenuItem onClick={() => onExportUsers("pdf")} data-testid="export-users-pdf">PDF (.pdf)</DropdownMenuItem>
                                <DropdownMenuItem onClick={() => onExportUsers("word")} data-testid="export-users-word">Word (.doc)</DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                        <div className="label-mono">{filteredUsers.length}/{users.length}</div>
                    </div>
                </div>
                <div className="bg-white border border-border rounded-md overflow-hidden">
                    <Table data-testid="admin-users-table">
                        <TableHeader>
                            <TableRow>
                                <TableHead className="w-10">
                                    <Checkbox
                                        checked={allSelected}
                                        onCheckedChange={toggleAll}
                                        aria-label="Select all referees"
                                        data-testid="select-all-users-checkbox"
                                    />
                                </TableHead>
                                <TableHead className="w-12"></TableHead>
                                <TableHead>{tr("admin.colUsername")}</TableHead>
                                <TableHead>{tr("common.surname")}</TableHead>
                                <TableHead>{tr("admin.colStatus")}</TableHead>
                                <TableHead className="w-16 text-center">{tr("admin.colNotes")}</TableHead>
                                <TableHead>{tr("admin.colEmail")}</TableHead>
                                <TableHead>{tr("admin.colBirthYear")}</TableHead>
                                <TableHead>{tr("admin.colMobile")}</TableHead>
                                <TableHead>{tr("admin.colLocation")}</TableHead>
                                <TableHead>{tr("admin.colSportExperience")}</TableHead>
                                <TableHead>{tr("admin.colPaymentMethod")}</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {filteredUsers.length === 0 && (
                                <TableRow><TableCell colSpan={12} className="text-center text-muted-foreground py-8" data-testid="admin-users-empty">{users.length === 0 ? tr("admin.noUsers") : tr("admin.noUsersMatch")}</TableCell></TableRow>
                            )}
                            {filteredUsers.map((u) => {
                                const token = localStorage.getItem("token");
                                const avatarUrl = u.avatar_path ? `${API_BASE}/files/${u.avatar_path}?auth=${token}` : null;
                                return (
                                <TableRow key={u.id} data-testid={`admin-user-row-${u.id}`}>
                                    <TableCell>
                                        <Checkbox
                                            checked={selected.has(u.id)}
                                            onCheckedChange={() => toggleOne(u.id)}
                                            aria-label={`Select ${u.name}`}
                                            data-testid={`select-user-checkbox-${u.id}`}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <button
                                            type="button"
                                            onClick={() => openEditUser(u)}
                                            className="w-9 h-9 rounded-full bg-accent border border-border overflow-hidden flex items-center justify-center hover:ring-2 hover:ring-primary/40 transition"
                                            data-testid={`admin-user-avatar-${u.id}`}
                                            aria-label={`Edit ${u.name}`}
                                        >
                                            {avatarUrl ? (
                                                <img src={avatarUrl} alt={u.name} className="w-full h-full object-cover" />
                                            ) : (
                                                <UserIcon className="w-4 h-4 text-muted-foreground" />
                                            )}
                                        </button>
                                    </TableCell>
                                    <TableCell className="font-medium">{u.name || ""}</TableCell>
                                    <TableCell className="font-medium">{u.surname || ""}</TableCell>
                                    <TableCell>
                                        <Popover>
                                            <PopoverTrigger asChild>
                                                <button
                                                    type="button"
                                                    className={`px-3 h-7 rounded-full text-[11px] font-medium tracking-wide transition-colors ${u.email_verified
                                                        ? "bg-green-500 text-white hover:bg-green-600"
                                                        : "bg-orange-500 text-white hover:bg-orange-600"}`}
                                                    data-testid={`admin-user-verify-btn-${u.id}`}
                                                >
                                                    {u.email_verified ? tr("admin.verified") : tr("admin.pending")}
                                                </button>
                                            </PopoverTrigger>
                                            <PopoverContent className="w-44 p-1.5" align="start" data-testid={`admin-user-verify-popover-${u.id}`}>
                                                <button
                                                    type="button"
                                                    onClick={() => setVerification(u.id, true)}
                                                    className="w-full flex items-center gap-2 px-3 py-2 text-sm rounded-sm hover:bg-accent/50 disabled:opacity-50"
                                                    disabled={u.email_verified}
                                                    data-testid={`admin-user-activate-${u.id}`}
                                                >
                                                    <span className="w-2 h-2 rounded-full bg-green-500" /> {tr("admin.activate")}
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => setVerification(u.id, false)}
                                                    className="w-full flex items-center gap-2 px-3 py-2 text-sm rounded-sm hover:bg-accent/50 disabled:opacity-50"
                                                    disabled={!u.email_verified}
                                                    data-testid={`admin-user-deactivate-${u.id}`}
                                                >
                                                    <span className="w-2 h-2 rounded-full bg-orange-500" /> {tr("admin.deactivate")}
                                                </button>
                                            </PopoverContent>
                                        </Popover>
                                    </TableCell>
                                    <TableCell className="text-center">
                                        <button
                                            type="button"
                                            onClick={() => openNotes(u)}
                                            className="inline-flex items-center justify-center w-9 h-9 rounded-md hover:bg-accent/40 transition-colors"
                                            title={u.admin_notes ? "View / edit notes" : "Add notes"}
                                            data-testid={`admin-user-notes-btn-${u.id}`}
                                        >
                                            <NotebookPen
                                                className={`w-5 h-5 ${u.admin_notes ? "text-green-700" : "text-orange-500"}`}
                                                strokeWidth={2.2}
                                                data-testid={`admin-user-notes-icon-${u.id}`}
                                            />
                                        </button>
                                    </TableCell>
                                    <TableCell className="mono text-xs">{u.email}</TableCell>
                                    <TableCell className="mono text-xs">{u.birth_year || "—"}</TableCell>
                                    <TableCell className="mono text-xs">{u.mobile}</TableCell>
                                    <TableCell className="text-sm">{u.location || "—"}</TableCell>
                                    <TableCell className="text-sm">
                                        {Array.isArray(u.sports_experience) && u.sports_experience.length > 0
                                            ? u.sports_experience
                                                .map((s) => {
                                                    const sport = (s.sport || "").trim();
                                                    const years = String(s.years ?? "").trim();
                                                    if (!sport) return null;
                                                    return years ? `${sport} - ${years}` : sport;
                                                })
                                                .filter(Boolean)
                                                .join(", ")
                                            : (u.interested_sports || "—")}
                                    </TableCell>
                                    <TableCell className="text-sm">
                                        {Array.isArray(u.payment_methods) && u.payment_methods.length > 0
                                            ? u.payment_methods
                                                .map((p) => {
                                                    const method = (p.method || "").trim();
                                                    const details = (p.details || "").trim();
                                                    if (!method) return null;
                                                    return details ? `${method} - ${details}` : method;
                                                })
                                                .filter(Boolean)
                                                .join(", ")
                                            : "—"}
                                    </TableCell>
                                </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </div>
            </div>

            <div>
                <div className="flex items-center justify-between mb-3 flex-wrap gap-3">
                    <h2 className="font-heading text-xl font-medium">{tr("admin.refereesAvailability")}</h2>
                </div>
                <div className="relative max-w-md mb-3">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
                    <Input
                        value={availSearch}
                        onChange={(e) => setAvailSearch(e.target.value)}
                        placeholder={tr("admin.availSearchPlaceholder")}
                        className="pl-9 h-10 rounded-md"
                        data-testid="admin-availability-search-input"
                    />
                </div>
                <div className="flex items-center justify-end mb-3 flex-wrap gap-3">
                    <div className="flex items-center gap-2 flex-wrap">
                        <div className="flex items-center gap-2">
                            <Filter className="w-3.5 h-3.5 text-muted-foreground" />
                            <Popover open={fromOpen} onOpenChange={setFromOpen}>
                                <PopoverTrigger asChild>
                                    <Button variant="outline" className="rounded-md h-10 mono text-xs gap-2" data-testid="filter-from-date-btn">
                                        <CalIcon className="w-3.5 h-3.5" /> {fromDate || tr("admin.filterFrom")}
                                    </Button>
                                </PopoverTrigger>
                                <PopoverContent className="w-auto p-0" align="start">
                                    <CalendarPicker
                                        mode="single"
                                        weekStartsOn={1}
                                        selected={fromObj}
                                        onSelect={(d) => { setFromDate(d ? ymd(d) : ""); setFromOpen(false); }}
                                        data-testid="filter-from-calendar"
                                    />
                                </PopoverContent>
                            </Popover>
                            <span className="label-mono text-muted-foreground">→</span>
                            <Popover open={toOpen} onOpenChange={setToOpen}>
                                <PopoverTrigger asChild>
                                    <Button variant="outline" className="rounded-md h-10 mono text-xs gap-2" data-testid="filter-to-date-btn">
                                        <CalIcon className="w-3.5 h-3.5" /> {toDate || tr("admin.filterTo")}
                                    </Button>
                                </PopoverTrigger>
                                <PopoverContent className="w-auto p-0" align="start">
                                    <CalendarPicker
                                        mode="single"
                                        weekStartsOn={1}
                                        selected={toObj}
                                        onSelect={(d) => { setToDate(d ? ymd(d) : ""); setToOpen(false); }}
                                        data-testid="filter-to-calendar"
                                    />
                                </PopoverContent>
                            </Popover>
                            {(fromDate || toDate) && (
                                <Button variant="ghost" size="icon" onClick={clearFilter} className="h-9 w-9" data-testid="filter-clear-btn">
                                    <X className="w-3.5 h-3.5" />
                                </Button>
                            )}
                        </div>
                        {selectedAvail.size > 0 && (
                            <Button variant="outline" onClick={() => setConfirmAvailOpen(true)}
                                className="rounded-md h-10 border-destructive/40 text-destructive hover:bg-destructive/10 hover:text-destructive"
                                data-testid="delete-selected-avail-btn">
                                <Trash2 className="w-4 h-4 mr-1.5" /> {tr("admin.deleteSelected", { n: selectedAvail.size })}
                            </Button>
                        )}
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button variant="outline" className="rounded-md h-10 bg-orange-100 border-orange-300 text-orange-900 hover:bg-orange-200 hover:text-orange-900" data-testid="export-trigger-btn">
                                    <Download className="w-4 h-4 mr-1.5" /> {tr("admin.exportLabel", { n: filtered.length })}
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" data-testid="export-menu">
                                <DropdownMenuItem onClick={() => onExport("csv")} data-testid="export-csv">CSV (.csv)</DropdownMenuItem>
                                <DropdownMenuItem onClick={() => onExport("excel")} data-testid="export-excel">Excel (.xls)</DropdownMenuItem>
                                <DropdownMenuItem onClick={() => onExport("pdf")} data-testid="export-pdf">PDF (.pdf)</DropdownMenuItem>
                                <DropdownMenuItem onClick={() => onExport("word")} data-testid="export-word">Word (.doc)</DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    </div>
                </div>
                <div className="bg-white border border-border rounded-md overflow-hidden">
                    <Table data-testid="admin-availability-table">
                        <TableHeader>
                            <TableRow>
                                <TableHead className="w-10">
                                    <Checkbox
                                        checked={allAvailSelected}
                                        onCheckedChange={toggleAvailAll}
                                        aria-label="Select all availability entries"
                                        data-testid="select-all-avail-checkbox"
                                    />
                                </TableHead>
                                <TableHead>{tr("admin.colDate")}</TableHead>
                                <TableHead>{tr("admin.colOption")}</TableHead>
                                <TableHead>{tr("admin.colUsername")}</TableHead>
                                <TableHead>{tr("common.surname")}</TableHead>
                                <TableHead className="w-16 text-center">{tr("admin.colNotes")}</TableHead>
                                <TableHead>{tr("admin.colEmail")}</TableHead>
                                <TableHead>{tr("admin.colMobile")}</TableHead>
                                <TableHead>{tr("common.yourAvailability")}</TableHead>
                                <TableHead>{tr("dashboard.suggestedPriceLabel")}</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {filtered.length === 0 && (
                                <TableRow><TableCell colSpan={10} className="text-center text-muted-foreground py-8" data-testid="admin-availability-empty">{tr("admin.noAvailability")}</TableCell></TableRow>
                            )}
                            {filtered.map((a) => (
                                <TableRow key={a.id} data-testid={`admin-avail-row-${a.id}`}>
                                    <TableCell className="w-10">
                                        <Checkbox
                                            checked={selectedAvail.has(a.id)}
                                            onCheckedChange={() => toggleAvailOne(a.id)}
                                            aria-label={`Select availability ${a.date}`}
                                            data-testid={`select-avail-checkbox-${a.id}`}
                                        />
                                    </TableCell>
                                    <TableCell className="mono text-sm">{formatDmy(a.date)}</TableCell>
                                    <TableCell className="text-sm">{a.work_option || "—"}</TableCell>
                                    <TableCell className="font-medium">{a.name || ""}</TableCell>
                                    <TableCell className="font-medium">{a.surname || ""}</TableCell>
                                    <TableCell className="text-center">
                                        <button
                                            type="button"
                                            onClick={() => openAvailNotes(a)}
                                            className="inline-flex items-center justify-center w-9 h-9 rounded-md hover:bg-accent/40 transition-colors"
                                            title={a.admin_notes ? "View / edit notes" : "Add notes"}
                                            data-testid={`admin-avail-notes-btn-${a.id}`}
                                        >
                                            <NotebookPen
                                                className={`w-5 h-5 ${a.admin_notes ? "text-green-700" : "text-blue-600"}`}
                                                strokeWidth={2.2}
                                                data-testid={`admin-avail-notes-icon-${a.id}`}
                                            />
                                        </button>
                                    </TableCell>
                                    <TableCell className="mono text-xs">{a.email}</TableCell>
                                    <TableCell className="mono text-xs">{a.mobile}</TableCell>
                                    <TableCell className="text-muted-foreground text-xs">{a.notes || "—"}</TableCell>
                                    <TableCell className="mono text-sm">{a.SuggestedPricePH || "—"}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </div>
            </div>

            <Dialog open={!!notesUser} onOpenChange={(o) => { if (!o) closeNotes(); }}>
                <DialogContent className="sm:max-w-lg rounded-md" data-testid="admin-user-notes-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-heading">{tr("admin.notesForUser", { name: [notesUser?.name, notesUser?.surname].filter(Boolean).join(" ") || "" })}</DialogTitle>
                    </DialogHeader>
                    <Textarea value={notesValue} onChange={(e) => setNotesValue(e.target.value)}
                        rows={6} maxLength={5000}
                        placeholder={tr("admin.userNotesPlaceholder")}
                        className="rounded-md min-h-[160px]"
                        data-testid="admin-user-notes-textarea" />
                    <div className="flex justify-end gap-2 pt-2">
                        <Button variant="outline" onClick={closeNotes} disabled={savingNotes} data-testid="admin-user-notes-cancel">{tr("common.cancel")}</Button>
                        <Button onClick={saveNotes} disabled={savingNotes} className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="admin-user-notes-save">
                            {savingNotes ? tr("common.saving") : tr("admin.saveNotes")}
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>

            <Dialog open={!!availNotesRow} onOpenChange={(o) => { if (!o) closeAvailNotes(); }}>
                <DialogContent className="sm:max-w-lg rounded-md" data-testid="admin-avail-notes-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-heading">
                            {tr("common.notes")} — {[availNotesRow?.name, availNotesRow?.surname].filter(Boolean).join(" ")} · {availNotesRow?.date} {availNotesRow?.start_time}{availNotesRow?.end_time ? `–${availNotesRow.end_time}` : ""}
                        </DialogTitle>
                    </DialogHeader>
                    <Textarea value={availNotesValue} onChange={(e) => setAvailNotesValue(e.target.value)}
                        rows={6} maxLength={5000}
                        placeholder={tr("admin.availNotesPlaceholder")}
                        className="rounded-md min-h-[160px]"
                        data-testid="admin-avail-notes-textarea" />
                    <div className="flex justify-end gap-2 pt-2">
                        <Button variant="outline" onClick={closeAvailNotes} disabled={savingAvailNotes} data-testid="admin-avail-notes-cancel">{tr("common.cancel")}</Button>
                        <Button onClick={saveAvailNotes} disabled={savingAvailNotes} className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="admin-avail-notes-save">
                            {savingAvailNotes ? tr("common.saving") : tr("admin.saveNotes")}
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>


            <AlertDialog open={confirmAvailOpen} onOpenChange={setConfirmAvailOpen}>
                <AlertDialogContent data-testid="confirm-delete-avail-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle>{tr("admin.confirmDeleteAvailTitle", { n: selectedAvail.size })}</AlertDialogTitle>
                        <AlertDialogDescription>
                            {tr("admin.confirmDeleteAvailBody", { n: selectedAvail.size })}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel disabled={deletingAvail} data-testid="confirm-delete-avail-cancel">{tr("common.cancel")}</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={(e) => { e.preventDefault(); performDeleteAvailability(); }}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                            disabled={deletingAvail}
                            data-testid="confirm-delete-avail-action"
                        >
                            {deletingAvail ? tr("common.deleting") : tr("admin.confirmDeleteAction")}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>


            <Dialog open={!!editUser} onOpenChange={(o) => { if (!o) { setEditUser(null); setEditConfirmOpen(false); } }}>
                <DialogContent className="sm:max-w-3xl rounded-md max-h-[90vh] overflow-y-auto" data-testid="admin-user-edit-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-heading">
                            {tr("admin.editProfileTitle", { name: [editUser?.name, editUser?.surname].filter(Boolean).join(" ") || "" })}
                        </DialogTitle>
                    </DialogHeader>
                    <div className="grid sm:grid-cols-2 gap-3">
                        <div className="space-y-1">
                            <Label htmlFor="edit-name">{tr("common.username")}</Label>
                            <Input id="edit-name" value={editForm.name}
                                onChange={(e) => setEditForm((p) => ({ ...p, name: e.target.value }))}
                                data-testid="admin-edit-name" />
                        </div>
                        <div className="space-y-1">
                            <Label htmlFor="edit-surname">{tr("common.surname")}</Label>
                            <Input id="edit-surname" value={editForm.surname}
                                onChange={(e) => setEditForm((p) => ({ ...p, surname: e.target.value }))}
                                data-testid="admin-edit-surname" />
                        </div>
                        <div className="space-y-1 sm:col-span-2">
                            <Label htmlFor="edit-email">{tr("admin.colEmail")}</Label>
                            <Input id="edit-email" type="email" value={editForm.email}
                                onChange={(e) => setEditForm((p) => ({ ...p, email: e.target.value }))}
                                data-testid="admin-edit-email" />
                        </div>
                        <div className="space-y-1">
                            <Label htmlFor="edit-mobile">{tr("admin.colMobile")}</Label>
                            <Input id="edit-mobile" value={editForm.mobile}
                                onChange={(e) => setEditForm((p) => ({ ...p, mobile: e.target.value }))}
                                data-testid="admin-edit-mobile" />
                        </div>
                        <div className="space-y-1">
                            <Label htmlFor="edit-birth-year">{tr("admin.colBirthYear")}</Label>
                            <Input id="edit-birth-year" type="number" min="1900" max="2100" value={editForm.birth_year}
                                onChange={(e) => setEditForm((p) => ({ ...p, birth_year: e.target.value }))}
                                data-testid="admin-edit-birth-year" />
                        </div>
                        <div className="space-y-1 sm:col-span-2">
                            <Label htmlFor="edit-location">{tr("admin.colLocation")}</Label>
                            <Input id="edit-location" value={editForm.location}
                                onChange={(e) => setEditForm((p) => ({ ...p, location: e.target.value }))}
                                data-testid="admin-edit-location" />
                        </div>
                    </div>

                    <div className="pt-2 border-t border-border">
                        <div className="flex items-center justify-between mb-2">
                            <Label className="text-sm font-medium">{tr("admin.colSportExperience")}</Label>
                            <Button type="button" variant="outline" size="sm"
                                onClick={() => setEditSports((p) => [...p, { sport: "", years: "" }])}
                                data-testid="admin-edit-sport-add" className="h-8">
                                <Plus className="w-3.5 h-3.5 mr-1" /> {tr("common.add")}
                            </Button>
                        </div>
                        <div className="space-y-2">
                            {editSports.length === 0 && (
                                <div className="text-xs text-muted-foreground italic">{tr("admin.noEntries")}</div>
                            )}
                            {editSports.map((s, idx) => (
                                <div key={idx} className="grid grid-cols-[1fr_140px_auto] gap-2 items-center" data-testid={`admin-edit-sport-row-${idx}`}>
                                    <Select value={s.sport} onValueChange={(v) => setEditSports((p) => p.map((x, i) => i === idx ? { ...x, sport: v } : x))}>
                                        <SelectTrigger className="h-9 rounded-md" data-testid={`admin-edit-sport-select-${idx}`}>
                                            <SelectValue placeholder={tr("admin.sportPlaceholder")} />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {SPORTS.map((sp) => <SelectItem key={sp} value={sp}>{sp}</SelectItem>)}
                                        </SelectContent>
                                    </Select>
                                    <Input value={s.years} maxLength={40} placeholder={tr("admin.yearsPlaceholder")}
                                        onChange={(e) => setEditSports((p) => p.map((x, i) => i === idx ? { ...x, years: e.target.value } : x))}
                                        className="h-9 rounded-md" data-testid={`admin-edit-sport-years-${idx}`} />
                                    <Button type="button" variant="outline" size="icon"
                                        onClick={() => setEditSports((p) => p.filter((_, i) => i !== idx))}
                                        className="h-9 w-9 border-destructive/30 text-destructive hover:bg-destructive/10"
                                        data-testid={`admin-edit-sport-remove-${idx}`}>
                                        <Minus className="w-3.5 h-3.5" />
                                    </Button>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="pt-2 border-t border-border">
                        <div className="flex items-center justify-between mb-2">
                            <Label className="text-sm font-medium">{tr("admin.colPaymentMethod")}</Label>
                            <Button type="button" variant="outline" size="sm"
                                onClick={() => setEditPayments((p) => [...p, { method: "", details: "" }])}
                                data-testid="admin-edit-payment-add" className="h-8">
                                <Plus className="w-3.5 h-3.5 mr-1" /> {tr("common.add")}
                            </Button>
                        </div>
                        <div className="space-y-2">
                            {editPayments.length === 0 && (
                                <div className="text-xs text-muted-foreground italic">{tr("admin.noEntries")}</div>
                            )}
                            {editPayments.map((pm, idx) => (
                                <div key={idx} className="grid grid-cols-[1fr_1fr_auto] gap-2 items-center" data-testid={`admin-edit-payment-row-${idx}`}>
                                    <Select value={pm.method} onValueChange={(v) => setEditPayments((p) => p.map((x, i) => i === idx ? { ...x, method: v } : x))}>
                                        <SelectTrigger className="h-9 rounded-md" data-testid={`admin-edit-payment-select-${idx}`}>
                                            <SelectValue placeholder={tr("admin.paymentMethodPlaceholder")} />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {PAYMENT_METHODS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                                        </SelectContent>
                                    </Select>
                                    <Input value={pm.details} maxLength={200} placeholder={tr("admin.paymentDetailsPlaceholder")}
                                        onChange={(e) => setEditPayments((p) => p.map((x, i) => i === idx ? { ...x, details: e.target.value } : x))}
                                        className="h-9 rounded-md" data-testid={`admin-edit-payment-details-${idx}`} />
                                    <Button type="button" variant="outline" size="icon"
                                        onClick={() => setEditPayments((p) => p.filter((_, i) => i !== idx))}
                                        className="h-9 w-9 border-destructive/30 text-destructive hover:bg-destructive/10"
                                        data-testid={`admin-edit-payment-remove-${idx}`}>
                                        <Minus className="w-3.5 h-3.5" />
                                    </Button>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="flex justify-end gap-2 pt-3">
                        <Button variant="outline" onClick={() => setEditUser(null)} disabled={editSaving} data-testid="admin-edit-cancel">{tr("common.cancel")}</Button>
                        <Button onClick={() => setEditConfirmOpen(true)} disabled={editSaving} className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="admin-edit-update">
                            {tr("admin.updateProfile")}
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>

            <AlertDialog open={editConfirmOpen} onOpenChange={setEditConfirmOpen}>
                <AlertDialogContent data-testid="admin-edit-confirm-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle>{tr("admin.editConfirmTitle")}</AlertDialogTitle>
                        <AlertDialogDescription>{tr("admin.editConfirmBody")}</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel disabled={editSaving} data-testid="admin-edit-confirm-no">{tr("admin.softDeleteConfirmNo")}</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={(e) => { e.preventDefault(); submitEditUser(); }}
                            disabled={editSaving}
                            data-testid="admin-edit-confirm-yes"
                        >
                            {editSaving ? tr("common.saving") : tr("admin.softDeleteConfirmYes")}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>


            <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
                <AlertDialogContent data-testid="confirm-delete-users-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle>{tr("admin.confirmDeleteTitle")}</AlertDialogTitle>
                        <AlertDialogDescription>
                            {tr("admin.confirmDeleteBody", { n: selected.size })}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel disabled={deleting} data-testid="confirm-delete-cancel-btn">{tr("common.cancel")}</AlertDialogCancel>
                        <AlertDialogAction
                            disabled={deleting}
                            onClick={(e) => { e.preventDefault(); confirmDelete(); }}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                            data-testid="confirm-delete-confirm-btn"
                        >
                            {deleting ? tr("common.deleting") : tr("admin.confirmDeleteAction")}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}

function TournamentDialog({ onCreated, editing, onClearEditing, cloning, onClearCloning }) {
    const { t: tr } = useLanguage();
    const [open, setOpen] = useState(false);
    const [startOpen, setStartOpen] = useState(false);
    const [endOpen, setEndOpen] = useState(false);
    const [form, setForm] = useState({
        name: "", sport_type: "", event_location: "", tournament_url: "", age_categories: "", format_categories: "", other_details: "",
        start_date: "", end_date: "", place: "", category: "men", age_group: "",
    });
    const [logo, setLogo] = useState(null);
    const [saving, setSaving] = useState(false);

    const isEditing = Boolean(editing);
    const isCloning = Boolean(cloning);

    useEffect(() => {
        const source = editing || cloning;
        if (source) {
            setForm({
                name: isCloning ? `${source.name || ""} (copy)`.trim() : (source.name || ""),
                sport_type: source.sport_type || "",
                event_location: source.event_location || source.place || "",
                tournament_url: source.tournament_url || "",
                age_categories: source.age_categories || "",
                format_categories: source.format_categories || "",
                other_details: source.other_details || "",
                start_date: source.start_date || "",
                end_date: source.end_date || "",
                place: source.place || "",
                category: source.category || "men",
                age_group: source.age_group || "",
            });
            setLogo(null);
            setOpen(true);
        }
    }, [editing, cloning, isCloning]);

    const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const submit = async (e) => {
        e.preventDefault();
        if (!form.start_date || !form.end_date) {
            toast.error(tr("admin.pickStartEnd"));
            return;
        }
        if (form.end_date < form.start_date) {
            toast.error(tr("admin.endBeforeStart"));
            return;
        }
        setSaving(true);
        try {
            const fd = new FormData();
            Object.entries(form).forEach(([k, v]) => fd.append(k, v));
            if (logo) fd.append("logo", logo);
            if (isEditing) {
                await api.put(`/tournaments/${editing.id}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
                toast.success(tr("admin.tournamentUpdated"));
            } else {
                await api.post("/tournaments", fd, { headers: { "Content-Type": "multipart/form-data" } });
                toast.success(tr("admin.tournamentCreated"));
            }
            handleClose(false);
            setForm({ name: "", sport_type: "", event_location: "", tournament_url: "", age_categories: "", format_categories: "", other_details: "", start_date: "", end_date: "", place: "", category: "men", age_group: "" });
            setLogo(null);
            onCreated?.();
        } catch (err) {
            toast.error(formatError(err.response?.data?.detail) || err.message);
        } finally {
            setSaving(false);
        }
    };

    const handleClose = (next) => {
        setOpen(next);
        if (!next) {
            if (isEditing) onClearEditing?.();
            if (isCloning) onClearCloning?.();
        }
    };

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <Button className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-md h-11" data-testid="add-tournament-btn">
                    <Plus className="w-4 h-4 mr-1" /> {tr("admin.addTournament")}
                </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-lg rounded-md max-h-[90vh] overflow-y-auto" data-testid="tournament-dialog">
                <DialogHeader>
                    <DialogTitle className="font-heading">{isEditing ? tr("tdialog.editTitle") : isCloning ? tr("tdialog.cloneTitle") : tr("tdialog.title")}</DialogTitle>
                </DialogHeader>
                <form onSubmit={submit} className="space-y-4" data-testid="tournament-form">
                    <div className="space-y-2">
                        <Label htmlFor="t-name">{tr("tdialog.eventName")}</Label>
                        <Input id="t-name" required value={form.name} onChange={update("name")} className="h-11 rounded-md" data-testid="t-name-input" />
                    </div>
                    <div className="space-y-2">
                        <Label htmlFor="t-tournament-url">{tr("tdialog.tournamentUrl")}</Label>
                        <Input id="t-tournament-url" value={form.tournament_url} onChange={update("tournament_url")}
                            placeholder="https://example.com"
                            className="h-11 rounded-md" data-testid="t-tournament-url-input" />
                    </div>
                    <div className="space-y-2">
                        <Label htmlFor="t-sport-type">{tr("tdialog.sportType")}</Label>
                        <Input id="t-sport-type" value={form.sport_type} onChange={update("sport_type")}
                            className="h-11 rounded-md" data-testid="t-sport-type-input" />
                    </div>
                    <div className="space-y-2">
                        <Label htmlFor="t-event-location">{tr("tdialog.eventLocation")}</Label>
                        <Input id="t-event-location" value={form.event_location} onChange={update("event_location")}
                            placeholder="https://maps.google.com/…"
                            className="h-11 rounded-md" data-testid="t-event-location-input" />
                    </div>
                    <div className="space-y-2">
                        <Label htmlFor="t-age-categories">{tr("tdialog.ageCategories")}</Label>
                        <Input id="t-age-categories" value={form.age_categories} onChange={update("age_categories")}
                            className="h-11 rounded-md" data-testid="t-age-categories-input" />
                    </div>
                    <div className="space-y-2">
                        <Label htmlFor="t-format-categories">{tr("tdialog.formatCategories")}</Label>
                        <Input id="t-format-categories" value={form.format_categories} onChange={update("format_categories")}
                            className="h-11 rounded-md" data-testid="t-format-categories-input" />
                    </div>
                    <div className="space-y-2">
                        <Label htmlFor="t-other-details">{tr("tdialog.otherDetails")}</Label>
                        <Textarea id="t-other-details" value={form.other_details} onChange={update("other_details")}
                            rows={5} maxLength={5000}
                            placeholder={tr("tdialog.otherDetailsPlaceholder")}
                            className="rounded-md min-h-[120px]" data-testid="t-other-details-input" />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <Label>{tr("tdialog.startDate")}</Label>
                            <Popover open={startOpen} onOpenChange={setStartOpen}>
                                <PopoverTrigger asChild>
                                    <Button type="button" variant="outline" className="w-full justify-start h-11 rounded-md mono text-xs gap-2" data-testid="t-start-date-btn">
                                        <CalIcon className="w-3.5 h-3.5" /> {form.start_date || tr("tdialog.pickStart")}
                                    </Button>
                                </PopoverTrigger>
                                <PopoverContent className="w-auto p-0" align="start">
                                    <CalendarPicker
                                        mode="single"
                                        weekStartsOn={1}
                                        selected={form.start_date ? new Date(`${form.start_date}T00:00:00`) : undefined}
                                        onSelect={(d) => {
                                            setForm({ ...form, start_date: d ? format(d, "yyyy-MM-dd") : "" });
                                            setStartOpen(false);
                                        }}
                                        data-testid="t-start-date-calendar"
                                    />
                                </PopoverContent>
                            </Popover>
                        </div>
                        <div className="space-y-2">
                            <Label>{tr("tdialog.endDate")}</Label>
                            <Popover open={endOpen} onOpenChange={setEndOpen}>
                                <PopoverTrigger asChild>
                                    <Button type="button" variant="outline" className="w-full justify-start h-11 rounded-md mono text-xs gap-2" data-testid="t-end-date-btn">
                                        <CalIcon className="w-3.5 h-3.5" /> {form.end_date || tr("tdialog.pickEnd")}
                                    </Button>
                                </PopoverTrigger>
                                <PopoverContent className="w-auto p-0" align="start">
                                    <CalendarPicker
                                        mode="single"
                                        weekStartsOn={1}
                                        selected={form.end_date ? new Date(`${form.end_date}T00:00:00`) : undefined}
                                        onSelect={(d) => {
                                            setForm({ ...form, end_date: d ? format(d, "yyyy-MM-dd") : "" });
                                            setEndOpen(false);
                                        }}
                                        data-testid="t-end-date-calendar"
                                    />
                                </PopoverContent>
                            </Popover>
                        </div>
                    </div>
                    <div className="space-y-2">
                        <Label htmlFor="t-logo">{tr("tdialog.logo")}</Label>
                        <Input id="t-logo" type="file" accept="image/*" onChange={(e) => setLogo(e.target.files?.[0] || null)}
                            className="h-11 rounded-md" data-testid="t-logo-input" />
                    </div>
                    <div className="flex gap-3 pt-2">
                        <Button type="button" variant="outline" className="flex-1 h-11" onClick={() => setOpen(false)} data-testid="t-cancel-btn">
                            {tr("common.cancel")}
                        </Button>
                        <Button type="submit" disabled={saving}
                            className="flex-1 h-11 bg-primary text-primary-foreground hover:bg-primary/90" data-testid="t-save-btn">
                            {saving ? tr("common.saving") : tr("common.save")}
                        </Button>
                    </div>
                </form>
            </DialogContent>
        </Dialog>
    );
}
